import {
  ConeGeometry,
  CylinderGeometry,
  Group,
  IcosahedronGeometry,
  InstancedMesh,
  Matrix4,
  Quaternion,
  Vector3,
} from "three";

import {
  TREE_RENDER_CONFIG,
  TREE_VISUAL_ARCHETYPES,
  TREE_VISUAL_PROFILES,
  treeVisualArchetypeFor,
  treeVisualVariation,
  type TreeCrownLayerProfile,
  type TreeVisualArchetype,
  type TreeVisualProfile,
} from "../../config/trees.ts";
import type {
  RuntimeTileLodLevel,
  RuntimeTreeInstance,
} from "../../world/schema/runtimeTile.ts";
import type { CityMaterialLibrary } from "../materials/cityMaterials.ts";

export interface RuntimeTreeBatch {
  readonly object: Group;
  readonly treeCount: number;
}

const UP = new Vector3(0, 1, 0);

export function createRuntimeTreeBatch(
  tileId: string,
  trees: readonly Readonly<RuntimeTreeInstance>[],
  lod: RuntimeTileLodLevel,
  materials: CityMaterialLibrary,
): RuntimeTreeBatch | null {
  const visibleTrees = trees.filter((tree) =>
    tree.heightM >= TREE_RENDER_CONFIG.minimumVisibleTreeHeightM
  );
  if (visibleTrees.length === 0) {
    return null;
  }

  const object = new Group();
  object.name = `runtime-trees:${tileId}`;
  const placements = [...new Set(visibleTrees.map((tree) => tree.placement))]
    .sort();
  const treesByArchetype = new Map<
    TreeVisualArchetype,
    readonly Readonly<RuntimeTreeInstance>[]
  >();
  const archetypeCounts: Partial<Record<TreeVisualArchetype, number>> = {};
  for (const archetype of TREE_VISUAL_ARCHETYPES) {
    const matching = visibleTrees.filter((tree) =>
      treeVisualArchetypeFor(tree) === archetype
    );
    if (matching.length === 0) {
      continue;
    }
    treesByArchetype.set(archetype, matching);
    archetypeCounts[archetype] = matching.length;
  }
  object.userData = {
    sourceLayer: placements.length === 1
      ? placements[0]
      : "mixed-source-and-reference-derived-vegetation",
    treeCount: visibleTrees.length,
    lod,
    placements,
    archetypeCounts,
    archetypeBasis: "reference-led-visual-mix-not-surveyed-per-stem-species",
  };

  const archetypeBatches: Group[] = [];
  for (const archetype of TREE_VISUAL_ARCHETYPES) {
    const matching = treesByArchetype.get(archetype);
    if (matching === undefined) {
      continue;
    }
    archetypeBatches.push(createArchetypeBatch(
      object.name,
      archetype,
      matching,
      lod,
      materials,
    ));
  }
  for (const mesh of mergeCompatibleTreeMeshes(object.name, archetypeBatches)) {
    object.add(mesh);
  }

  return { object, treeCount: visibleTrees.length };
}

function mergeCompatibleTreeMeshes(
  parentName: string,
  batches: readonly Group[],
): readonly InstancedMesh[] {
  const sourceMeshes: InstancedMesh[] = [];
  for (const batch of batches) {
    for (const child of batch.children) {
      if (child instanceof InstancedMesh) sourceMeshes.push(child);
    }
  }
  const grouped = new Map<string, InstancedMesh[]>();
  for (const mesh of sourceMeshes) {
    mesh.geometry.computeBoundingBox();
    const bounds = mesh.geometry.boundingBox;
    const position = mesh.geometry.getAttribute("position");
    const index = mesh.geometry.getIndex();
    const materialKey = Array.isArray(mesh.material)
      ? mesh.material.map((material) => material.uuid).join(",")
      : mesh.material.uuid;
    const geometryKey = [
      position.count,
      index?.count ?? 0,
      bounds?.min.x.toFixed(4) ?? 0,
      bounds?.min.y.toFixed(4) ?? 0,
      bounds?.min.z.toFixed(4) ?? 0,
      bounds?.max.x.toFixed(4) ?? 0,
      bounds?.max.y.toFixed(4) ?? 0,
      bounds?.max.z.toFixed(4) ?? 0,
    ].join(":");
    const suffix = mesh.name.slice(mesh.name.lastIndexOf(":") + 1);
    const key = [
      suffix,
      materialKey,
      geometryKey,
      Number(mesh.castShadow),
      Number(mesh.receiveShadow),
    ].join("|");
    const entries = grouped.get(key) ?? [];
    entries.push(mesh);
    grouped.set(key, entries);
  }

  const result: InstancedMesh[] = [];
  const retainedGeometries = new Set();
  const matrix = new Matrix4();
  for (const [key, meshes] of grouped) {
    const first = meshes[0];
    if (first === undefined) continue;
    const instanceCount = meshes.reduce((sum, mesh) => sum + mesh.count, 0);
    const merged = new InstancedMesh(
      first.geometry,
      first.material,
      instanceCount,
    );
    const suffix = key.slice(0, key.indexOf("|"));
    merged.name = `${parentName}:batched-${result.length}:${suffix}`;
    merged.castShadow = first.castShadow;
    merged.receiveShadow = first.receiveShadow;
    let targetIndex = 0;
    for (const mesh of meshes) {
      for (let index = 0; index < mesh.count; index += 1) {
        mesh.getMatrixAt(index, matrix);
        merged.setMatrixAt(targetIndex, matrix);
        targetIndex += 1;
      }
    }
    merged.instanceMatrix.needsUpdate = true;
    merged.computeBoundingBox();
    merged.computeBoundingSphere();
    result.push(merged);
    retainedGeometries.add(first.geometry);
  }

  for (const mesh of sourceMeshes) {
    mesh.removeFromParent();
    if (!retainedGeometries.has(mesh.geometry)) mesh.geometry.dispose();
  }
  for (const batch of batches) batch.clear();
  return result;
}

function createArchetypeBatch(
  parentName: string,
  archetype: TreeVisualArchetype,
  trees: readonly Readonly<RuntimeTreeInstance>[],
  lod: RuntimeTileLodLevel,
  materials: CityMaterialLibrary,
): Group {
  const profile = TREE_VISUAL_PROFILES[archetype];
  const object = new Group();
  object.name = `${parentName}:${archetype}`;
  object.userData = { archetype, treeCount: trees.length, lod };

  const crownLayers = crownLayersFor(profile, lod);
  const trunkGeometry = new CylinderGeometry(
    1,
    archetype === "oak" ? 1.24 : 1.12,
    1,
    lod === 0 ? 7 : 5,
  );
  const crownGeometry = profile.crownShape === "conical"
    ? new ConeGeometry(1, 1, lod === 0 ? 7 : 5, 1)
    : new IcosahedronGeometry(1, lod === 0 ? 1 : 0);
  const branchGeometry = new CylinderGeometry(
    0.48,
    0.8,
    1,
    lod === 0 ? 5 : 4,
  );
  const seasonalClusterGeometry = new IcosahedronGeometry(1, 0);
  const trunks = lod === 2
    ? null
    : new InstancedMesh(
        trunkGeometry,
        trunkMaterialFor(profile, materials),
        trees.length,
      );
  const lowerCrowns = new InstancedMesh(
    crownGeometry,
    darkCrownMaterialFor(profile, materials),
    trees.length,
  );
  const upperLayerCount = Math.max(0, crownLayers.length - 1);
  const upperCrowns = upperLayerCount === 0
    ? null
    : new InstancedMesh(
        crownGeometry,
        lightCrownMaterialFor(profile, materials),
        trees.length * upperLayerCount,
      );
  const branches = lod === 2
    ? null
    : new InstancedMesh(
        branchGeometry,
        branchMaterialFor(profile, materials),
        trees.length * profile.branchCount,
      );
  const blossomTrees = lod === 0 && profile.springBlossomFrequency > 0
    ? trees.filter((tree) =>
        treeVisualVariation(tree.id, "blossom") <
          1 / profile.springBlossomFrequency
      )
    : [];
  const blossoms = blossomTrees.length === 0
    ? null
    : new InstancedMesh(
        seasonalClusterGeometry,
        materials.treeBlossom,
        blossomTrees.length,
      );
  const snowCaps = lod === 2
    ? null
    : new InstancedMesh(
        seasonalClusterGeometry,
        materials.treeSnow,
        trees.length,
      );

  if (trunks !== null) {
    configureMesh(trunks, `${object.name}:trunks`, lod === 0);
  } else {
    trunkGeometry.dispose();
  }
  configureMesh(lowerCrowns, `${object.name}:crowns`, lod === 0);
  if (upperCrowns !== null) {
    configureMesh(upperCrowns, `${object.name}:upper-crowns`, lod === 0);
  }
  if (branches !== null) {
    configureMesh(
      branches,
      `${object.name}:winter-branches`,
      lod === 0,
    );
  } else {
    branchGeometry.dispose();
  }
  if (blossoms !== null) {
    configureMesh(blossoms, `${object.name}:spring-blossoms`, false);
  }
  if (snowCaps !== null) {
    configureMesh(snowCaps, `${object.name}:winter-snow-caps`, lod === 0);
  } else if (blossoms === null) {
    seasonalClusterGeometry.dispose();
  }

  const matrix = new Matrix4();
  const position = new Vector3();
  const rotation = new Quaternion();
  const branchRotation = new Quaternion();
  const branchDirection = new Vector3();
  const scale = new Vector3();
  let upperCrownIndex = 0;
  let branchInstanceIndex = 0;
  for (const [treeIndex, tree] of trees.entries()) {
    rotation.setFromAxisAngle(UP, tree.yawRadians);
    const trunkHeightM = tree.heightM * profile.trunkHeightRatio;
    if (trunks !== null) {
      const trunkRadiusM = tree.trunkDiameterM === undefined
        ? tree.crownRadiusM * profile.trunkRadiusToCrownRatio
        : Math.max(0.035, Math.min(1.1, tree.trunkDiameterM / 2));
      scale.set(
        trunkRadiusM,
        trunkHeightM,
        trunkRadiusM,
      );
      position.set(
        tree.position.x,
        tree.baseElevationM + trunkHeightM / 2,
        tree.position.z,
      );
      matrix.compose(position, rotation, scale);
      trunks.setMatrixAt(treeIndex, matrix);
    }

    for (const [layerIndex, layer] of crownLayers.entries()) {
      applyCrownMatrix(
        matrix,
        position,
        rotation,
        scale,
        tree,
        layer,
        layerIndex,
        lod,
      );
      if (layerIndex === 0) {
        lowerCrowns.setMatrixAt(treeIndex, matrix);
      } else if (upperCrowns !== null) {
        upperCrowns.setMatrixAt(upperCrownIndex, matrix);
        upperCrownIndex += 1;
      }
    }

    if (branches !== null) {
      for (
        let branchIndex = 0;
        branchIndex < profile.branchCount;
        branchIndex += 1
      ) {
        const angle = tree.yawRadians +
          branchIndex * (Math.PI * 2 / profile.branchCount) +
          Math.PI * 0.19;
        branchDirection.set(
          Math.cos(angle) * 0.74,
          0.64 + (branchIndex % 2) * 0.1,
          Math.sin(angle) * 0.74,
        ).normalize();
        branchRotation.setFromUnitVectors(UP, branchDirection);
        const branchLength = tree.crownRadiusM *
          profile.branchLengthScale *
          (0.82 + (branchIndex % 3) * 0.08);
        scale.set(
          tree.crownRadiusM *
            profile.trunkRadiusToCrownRatio *
            0.42,
          branchLength,
          tree.crownRadiusM *
            profile.trunkRadiusToCrownRatio *
            0.42,
        );
        position.set(
          tree.position.x + branchDirection.x * branchLength * 0.42,
          tree.baseElevationM +
            trunkHeightM * (0.7 + (branchIndex % 2) * 0.08),
          tree.position.z + branchDirection.z * branchLength * 0.42,
        );
        matrix.compose(position, branchRotation, scale);
        branches.setMatrixAt(branchInstanceIndex, matrix);
        branchInstanceIndex += 1;
      }
    }

    if (snowCaps !== null) {
      const topLayer = profile.crownLayers.at(-1)!;
      const topOffset = rotatedLayerOffset(tree, topLayer);
      rotation.setFromAxisAngle(UP, tree.yawRadians);
      scale.set(
        tree.crownRadiusM * topLayer.widthScale * 0.74,
        tree.crownRadiusM * 0.14,
        tree.crownRadiusM * topLayer.depthScale * 0.7,
      );
      position.set(
        tree.position.x + topOffset.x,
        tree.baseElevationM +
          tree.heightM * topLayer.heightRatio +
          tree.crownRadiusM * topLayer.verticalScale * 0.48,
        tree.position.z + topOffset.z,
      );
      matrix.compose(position, rotation, scale);
      snowCaps.setMatrixAt(treeIndex, matrix);
    }
  }

  if (blossoms !== null) {
    for (const [index, tree] of blossomTrees.entries()) {
      const angle = treeVisualVariation(tree.id, "blossom-angle") *
        Math.PI * 2;
      rotation.setFromAxisAngle(UP, angle);
      scale.setScalar(tree.crownRadiusM * 0.28);
      position.set(
        tree.position.x + Math.cos(angle) * tree.crownRadiusM * 0.42,
        tree.baseElevationM + tree.heightM * 0.82,
        tree.position.z + Math.sin(angle) * tree.crownRadiusM * 0.42,
      );
      matrix.compose(position, rotation, scale);
      blossoms.setMatrixAt(index, matrix);
    }
  }

  addUpdatedMesh(object, trunks);
  addUpdatedMesh(object, lowerCrowns);
  addUpdatedMesh(object, upperCrowns);
  addUpdatedMesh(object, branches);
  addUpdatedMesh(object, blossoms);
  addUpdatedMesh(object, snowCaps);
  return object;
}

function crownLayersFor(
  profile: Readonly<TreeVisualProfile>,
  lod: RuntimeTileLodLevel,
): readonly Readonly<TreeCrownLayerProfile>[] {
  const count = lod === 0
    ? profile.crownLayers.length
    : lod === 1
      ? profile.mediumLodCrownLayerCount
      : 1;
  return profile.crownLayers.slice(0, count);
}

function applyCrownMatrix(
  matrix: Matrix4,
  position: Vector3,
  rotation: Quaternion,
  scale: Vector3,
  tree: Readonly<RuntimeTreeInstance>,
  layer: Readonly<TreeCrownLayerProfile>,
  layerIndex: number,
  lod: RuntimeTileLodLevel,
): void {
  const offset = rotatedLayerOffset(tree, layer);
  const yawVariation =
    (treeVisualVariation(tree.id, `crown-yaw:${layerIndex}`) - 0.5) *
    TREE_RENDER_CONFIG.crownYawVariationRadians * 2;
  const widthVariation = 1 +
    (treeVisualVariation(tree.id, `crown-width:${layerIndex}`) - 0.5) *
      TREE_RENDER_CONFIG.crownWidthVariation * 2;
  const depthVariation = 1 +
    (treeVisualVariation(tree.id, `crown-depth:${layerIndex}`) - 0.5) *
      TREE_RENDER_CONFIG.crownDepthVariation * 2;
  const distantScale = lod === 2
    ? TREE_RENDER_CONFIG.distantCrownScale
    : 1;
  rotation.setFromAxisAngle(UP, tree.yawRadians + yawVariation);
  scale.set(
    tree.crownRadiusM * layer.widthScale * widthVariation * distantScale,
    tree.crownRadiusM * layer.verticalScale,
    tree.crownRadiusM * layer.depthScale * depthVariation * distantScale,
  );
  position.set(
    tree.position.x + offset.x,
    tree.baseElevationM + tree.heightM * layer.heightRatio,
    tree.position.z + offset.z,
  );
  matrix.compose(position, rotation, scale);
}

function rotatedLayerOffset(
  tree: Readonly<RuntimeTreeInstance>,
  layer: Readonly<TreeCrownLayerProfile>,
): Readonly<{ x: number; z: number }> {
  const cosine = Math.cos(tree.yawRadians);
  const sine = Math.sin(tree.yawRadians);
  return {
    x: (layer.offsetX * cosine - layer.offsetZ * sine) * tree.crownRadiusM,
    z: (layer.offsetX * sine + layer.offsetZ * cosine) * tree.crownRadiusM,
  };
}

function trunkMaterialFor(
  profile: Readonly<TreeVisualProfile>,
  materials: CityMaterialLibrary,
) {
  return profile.materialFamily === "birch"
    ? materials.treeBirchTrunk
    : profile.materialFamily === "evergreen"
      ? materials.treeEvergreenTrunk
      : materials.treeTrunk;
}

function branchMaterialFor(
  profile: Readonly<TreeVisualProfile>,
  materials: CityMaterialLibrary,
) {
  return profile.materialFamily === "birch"
    ? materials.treeBirchTrunk
    : profile.materialFamily === "evergreen"
      ? materials.treeEvergreenTrunk
      : materials.treeBranch;
}

function darkCrownMaterialFor(
  profile: Readonly<TreeVisualProfile>,
  materials: CityMaterialLibrary,
) {
  return profile.materialFamily === "evergreen"
    ? materials.treeEvergreenDark
    : materials.treeCrownDark;
}

function lightCrownMaterialFor(
  profile: Readonly<TreeVisualProfile>,
  materials: CityMaterialLibrary,
) {
  return profile.materialFamily === "evergreen"
    ? materials.treeEvergreenLight
    : materials.treeCrownLight;
}

function configureMesh(
  mesh: InstancedMesh,
  name: string,
  castShadow: boolean,
): void {
  mesh.name = name;
  mesh.castShadow = castShadow;
  mesh.receiveShadow = castShadow;
}

function addUpdatedMesh(object: Group, mesh: InstancedMesh | null): void {
  if (mesh === null) {
    return;
  }
  mesh.instanceMatrix.needsUpdate = true;
  object.add(mesh);
}
